import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { useAuth } from '@/features/auth/store'
import { CustomerFormDialog } from '@/features/customers/CustomerFormDialog'
import {
  CUSTOMER_STATUSES,
  CUSTOMER_TIERS,
  exportCustomers,
  listCustomers,
  type CustomerFilters,
  type CustomerRead,
} from '@/features/customers/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const TIER_TONE: Record<string, string> = {
  standard: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  silver: 'bg-slate-300 text-slate-800 dark:bg-slate-600 dark:text-slate-100',
  gold: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  platinum: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
}

const columns: Column<CustomerRead>[] = [
  {
    key: 'code',
    header: 'Code',
    className: 'font-mono text-xs',
    sortValue: (customer) => customer.code,
    cell: (customer) => (
      <Link to={`/customers/${customer.id}`} className="hover:underline">
        {customer.code}
      </Link>
    ),
  },
  {
    key: 'name',
    header: 'Customer',
    className: 'font-medium',
    sortValue: (customer) => customer.name,
    cell: (customer) => (
      <Link to={`/customers/${customer.id}`} className="hover:underline">
        {customer.name}
      </Link>
    ),
  },
  {
    key: 'city',
    header: 'City',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (customer) => [customer.city, customer.state].filter(Boolean).join(', ') || '—',
  },
  {
    key: 'tier',
    header: 'Tier',
    cell: (customer) => (
      <span className={`rounded px-2 py-0.5 text-xs font-medium ${TIER_TONE[customer.tier] ?? TIER_TONE.standard}`}>
        {customer.tier[0].toUpperCase() + customer.tier.slice(1)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (customer) =>
      customer.status === 'active' ? (
        <span className="rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          Active
        </span>
      ) : (
        <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {customer.status}
        </span>
      ),
  },
  {
    key: 'assigned_rep_id',
    header: 'Rep',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (customer) => (customer.assigned_rep_id ? 'Assigned' : '— Unassigned'),
  },
]

/**
 * The account book, as a list that leads to the record rather than to an edit form.
 *
 * Reassignment does not get a column here -- it is gated on `customers:delete`, one cell
 * finer than most of this screen needs, so it lives on the detail page next to the record
 * it changes instead of turning every row into a dropdown most viewers cannot use.
 */
export function Customers() {
  const can = useAuth((s) => s.can)
  const [filters, setFilters] = useState<CustomerFilters>({ page: 1, status: 'active' })
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['customers', filters],
    queryFn: () => listCustomers(filters),
    retry: false,
  })

  const customers = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<CustomerFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} customer${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {can('customers', 'create') && (
          <Button onClick={() => setDialogOpen(true)}>New customer</Button>
        )}
      </header>

      <Card>
        <CardHeader
          title="Accounts"
          description="Open a customer to see their orders, invoices, and who carries the account."
        />
        <DataTable
          rows={customers}
          columns={columns}
          getRowId={(customer) => customer.id}
          caption="Customers"
          isLoading={isLoading}
          error={error}
          emptyMessage="No customers match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="customer-search">
                Search customers
              </label>
              <input
                id="customer-search"
                className={SELECT_CLASS}
                placeholder="Code or name…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') set({ q: search })
                }}
                onBlur={() => set({ q: search })}
              />

              <label className="sr-only" htmlFor="customer-tier">
                Tier
              </label>
              <select
                id="customer-tier"
                className={SELECT_CLASS}
                value={filters.tier ?? ''}
                onChange={(event) => set({ tier: event.target.value })}
              >
                <option value="">Any tier</option>
                {CUSTOMER_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier[0].toUpperCase() + tier.slice(1)}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="customer-status">
                Status
              </label>
              <select
                id="customer-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value })}
              >
                <option value="">Any status</option>
                {CUSTOMER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status[0].toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={filters.unassigned ?? false}
                  onChange={(event) => set({ unassigned: event.target.checked || undefined })}
                />
                Unassigned only
              </label>

              {(filters.q || filters.tier || filters.status !== 'active' || filters.unassigned) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setFilters({ page: 1, status: 'active' })
                  }}
                >
                  Clear
                </Button>
              )}

              <ExportButton
                filename="customers"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportCustomers(filters, format)}
                errorMessage="Could not export the customer list."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      {dialogOpen && <CustomerFormDialog open onClose={() => setDialogOpen(false)} />}
    </div>
  )
}
